import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { KEYBOARD, MOUSE, WINDOW } from '@finder/shared/constants/user-input.constants';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'finder-list-drawer',
    templateUrl: './list-drawer.component.html',
    styleUrls: ['./list-drawer.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
    ]
})
export class ListDrawerComponent implements OnInit, OnDestroy {

    @Input() sourceElement: HTMLElement;
    @Input() closeOnClickOutside = false;
    @Input() hideCaret = false;
    @Input() displayedState = false;
    @Output() public displayedStateChange = new EventEmitter<boolean>();

    caretPosition: string;

    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private winRef: WindowRef,
        private zone: NgZone,
        private ssrService: SsrService,
        @Inject(DOCUMENT) private document: Document
    ) { }

    /* istanbul ignore next */
    getElementRef(): Element {
        return this.elementRef.nativeElement;
    }

    ngOnInit() {
        if (this.sourceElement) {
            this.setUpListeners();
        }
    }

    setUpListeners() {
        this.setClickOutsideHandler();
        this.setCloseOnEscapeHandler();
        this.setCaretPositionHandler();
    }

    setClickOutsideHandler() {
        if (!this.closeOnClickOutside) {
            return;
        }
        fromEvent(this.document, MOUSE.CLICK)
            .pipe(
                filter(() => this.displayedState),
                takeUntil(this.destroy$)
            )
            .subscribe(($event: any) => {
                /**
                 * https://chromestatus.com/feature/5726124632965120
                 * event.path is a deprecated property. use composedPath or "normalizeEventPartial"
                 * instead.
                 */
                const eventPath = $event.composedPath();
                const elementRefInPath = eventPath.includes(this.getElementRef())
                    || eventPath.includes(this.sourceElement);
                if (!elementRefInPath) {
                    this.setDisplayedState(false);
                }
            });
    }

    setCloseOnEscapeHandler() {
        fromEvent(this.document, KEYBOARD.KEY_UP)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(($event: any) => {
                if ($event.keyCode === KEYBOARD.ESCAPE_KEY_CODE && this.displayedState) {
                    this.sourceElement.focus();
                    this.setDisplayedState(false);
                }
            });
    }

    setCaretPositionHandler() {
        // @SSR: This will adjust the position of the drawer. We can leave that to the browser
        if (this.ssrService.isSSR()) {
            return;
        }

        this.setCaretPosition();

        this.zone.runOutsideAngular(() => {
            merge(
                fromEvent(this.winRef.nativeWindow, WINDOW.RESIZE),
                fromEvent(this.winRef.nativeWindow, WINDOW.SCROLL)
            )
            .pipe(
                debounceTime(150),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.setCaretPosition());
        });
    }

    setCaretPosition() {
        const position = this.sourceElement.getBoundingClientRect().left + (this.sourceElement.offsetWidth / 2);
        this.caretPosition = `${position - 12}px`;
    }

    ngOnDestroy() {
        this.destroy$.next();
    }

    toggleDisplayedState() {
        this.setDisplayedState(!this.displayedState);
    }

    setDisplayedState(value: boolean) {
        this.displayedState = value;
        this.displayedStateChange.emit(value);
    }
}

